import { Anchor } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { postFeedback } from '../../../../services/FeedbackService';
import { BookingResponseModel } from '../../../../types/BookingModels';
import { PostFeedbackInputModel } from '../../../../types/FeedbackModels';
import { ErrorModal } from '../error/ErrorModal';
import { SuccessModal } from '../../../../components/modals/SuccessModal';
import { FeedbackFormSheet } from './FeedbackFormSheet';

interface LeaveFeedbackButtonProps {
  groupedBookings: BookingResponseModel[];
  profileId: string;
  token: string;
  refreshBookings: () => void;
}

const getFeedbackGiven = (
  booking: BookingResponseModel,
  isProfessional: boolean
): boolean => {
  if (isProfessional) {
    return Boolean(
      booking.professionalFeedbackGiven ?? booking.ProfessionalFeedbackGiven
    );
  }

  return Boolean(booking.userFeedbackGiven ?? booking.UserFeedbackGiven);
};

export const LeaveFeedbackButton = ({
  groupedBookings,
  profileId,
  token,
  refreshBookings,
}: LeaveFeedbackButtonProps) => {
  const booking = groupedBookings[0];
  const finalBooking = groupedBookings[groupedBookings.length - 1];

  const [opened, setOpened] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');

  const closeAndResetForm = () => {
    setOpened(false);
    setRating(0);
    setComment('');
  };

  const isProfessional = useMemo(
    () => profileId === booking.professionalId,
    [profileId, booking.professionalId]
  );

  const hasGivenFeedback = useMemo(
    () => getFeedbackGiven(booking, isProfessional),
    [booking, isProfessional]
  );

  const callTookPlace = useMemo(() => {
    const now = Date.now();
    const endTime = new Date(finalBooking.endIso).getTime();
    const isPaidOrFree =
      booking.rate === 0 || booking.paymentStatus === 'succeeded';

    return (
      booking.accepted &&
      !booking.isCancelled &&
      isPaidOrFree &&
      endTime < now
    );
  }, [
    booking.accepted,
    booking.isCancelled,
    booking.paymentStatus,
    booking.rate,
    finalBooking.endIso,
  ]);

  const callDurationMinutes = useMemo(() => {
    const startTime = new Date(booking.startIso).getTime();
    const endTime = new Date(finalBooking.endIso).getTime();

    return Math.floor((endTime - startTime) / 60000);
  }, [booking.startIso, finalBooking.endIso]);

  const canLeaveFeedback =
    callTookPlace && callDurationMinutes > 10 && !hasGivenFeedback;

  const isCommentValid = comment.length <= 500;
  const canSubmit = rating >= 1 && rating <= 5 && isCommentValid;

  const { mutate, isPending, isError, reset } = useMutation({
    mutationFn: async () => {
      const input: PostFeedbackInputModel = {
        bookingId: booking.id,
        rating,
        comment: comment.trim(),
      };

      await postFeedback(input, token);
    },
    onSuccess: () => {
      setOpened(false);
      setSubmitted(true);
      setShowSuccess(true);
      setRating(0);
      setComment('');
      refreshBookings();
    },
    onError: (error) => {
      setOpened(false);
      console.error('Error submitting feedback:', error);
    },
  });

  if (submitted) {
    return (
      <SuccessModal
        open={showSuccess}
        onClose={() => {
          setShowSuccess(false);
          setSubmitted(false);
        }}
        title="Thank you"
        message="Thank you for your feedback"
      />
    );
  }

  if (!canLeaveFeedback) {
    return null;
  }

  return (
    <>
      <ErrorModal
        isInError={isError}
        errorMessage="An error has occurred. Please try again."
        dismissAction={() => reset()}
        retryAction={() => mutate()}
        isLoading={isPending}
        testId="feedback-error-modal"
      />

      <Anchor
        onClick={() => setOpened(true)}
        ta="end"
        c="dimmed"
        fz="sm"
        td="underline"
        data-testid={`leave-feedback-link-${booking.id}`}
      >
        Leave Feedback
      </Anchor>

      <FeedbackFormSheet
        opened={opened}
        onClose={closeAndResetForm}
        rating={rating}
        onRatingChange={setRating}
        comment={comment}
        onCommentChange={setComment}
        isSubmitting={isPending}
        canSubmit={canSubmit}
        onSubmit={() => mutate()}
        testIdPrefix={`feedback-${booking.id}`}
      />
    </>
  );
};
